import { autoUpdate, size, useFloating } from '@floating-ui/react-dom'
import { Popover, PopoverButton, PopoverPanel, Transition } from '@headlessui/react'
import { Fragment, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ConfirmDialog } from '@/modules/shared/components/confirm-dialog/ConfirmDialog'
import { DEFAULT_ROWS_PER_PAGE, ROWS_PER_PAGE_OPTIONS } from '@/modules/shared/constants'
import { ChevronDownIcon } from '@/modules/shared/icons/ChevronDownIcon'
import { Portal } from '@/modules/shared/layouts/Portal'
import { Themes } from '@/modules/shared/types/Themes'

interface RowsPerPageOptionsProps {
  rowsPerPage: number
  onRowsPerPageChange: (rowsPerPage: number) => void
  disabled?: boolean
}

export function RowsPerPageOptions({ rowsPerPage, onRowsPerPageChange, disabled }: RowsPerPageOptionsProps) {
  const { t } = useTranslation()
  const [comingSoon, setComingSoon] = useState(false)
  const {
    x,
    y,
    strategy,
    refs: { setReference, setFloating },
  } = useFloating({
    placement: 'bottom',
    whileElementsMounted: autoUpdate,
    middleware: [
      size({
        apply({ rects, elements }) {
          Object.assign(elements.floating.style, {
            width: `${rects.reference.width}px`,
          })
        },
      }),
    ],
  })

  return (
    <>
      <Popover className="relative flex items-center justify-center">
        {({ close }) => (
          <>
            <PopoverButton
              className="flex items-center gap-x-2 rounded-md bg-gray-200 px-4 py-2 hover:brightness-95"
              ref={setReference}
              data-testid="rows-per-page-btn"
              onClick={() => {
                if (disabled) setComingSoon(true)
              }}
            >
              <span className="text-sm">
                {t('requisition.details.rowsPerPage.information', {
                  defaultValue_one: '{{ count }} Row per Page',
                  defaultValue_many: '{{ count }} Rows per Page',
                  defaultValue_other: '{{ count }} Rows per Page',
                  count: rowsPerPage || DEFAULT_ROWS_PER_PAGE,
                })}
              </span>
              <ChevronDownIcon className="size-6" />
            </PopoverButton>
            <Portal>
              <Transition
                as={Fragment}
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
              >
                <PopoverPanel
                  data-testid="more-options-menu"
                  className="z-[99999] rounded-md border bg-white shadow-lg"
                  ref={setFloating}
                  style={{
                    position: strategy,
                    top: y ?? 0,
                    left: x ?? 0,
                  }}
                >
                  <div className="flex w-full flex-col">
                    {ROWS_PER_PAGE_OPTIONS.map((option) => (
                      <button
                        key={option}
                        onClick={() => {
                          onRowsPerPageChange(option)
                          close()
                        }}
                        className="w-full px-4 py-2 text-start hover:bg-gray-100"
                      >
                        {option}
                      </button>
                    ))}
                  </div>
                </PopoverPanel>
              </Transition>
            </Portal>
          </>
        )}
      </Popover>
      <ConfirmDialog
        theme={Themes.Primary}
        isOpen={comingSoon}
        setIsOpen={setComingSoon}
        title={t('general.comingSoon', 'Coming Soon!')}
        description={t(
          'requisition.details.rowsPerPage.comingSoonContent',
          "Soon you will be able to change the Rows per Page here, but we're still working on it."
        )}
        primaryButtonLabel={t('general.okayGotIt', 'Okay, Got It')}
        onPrimaryBtnClick={() => setComingSoon(false)}
      />
    </>
  )
}
